import React from 'react';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { CHAPTERS } from '../data/chapters';
import ChapterLaunchBadge from './ChapterLaunchBadge';

function launchBadgeForChapterId(id) {
  return CHAPTERS.find((c) => c.id === id)?.launchBadge;
}

const chapters = [
  {
    id: 'fertility',
    emoji: '🌸',
    title: 'Fertility',
    sub: 'IVF · IUI · Egg freezing · Medicated cycles',
    ageBadge: '20s → 40s',
    badgeBg: '#FDE8F0',
    badgeColor: '#9B3060',
    accentColor: '#D4537E',
    img: '/images/chapter-heroes/fertility-homepage.png',
    imgObjectPosition: 'center center',
    imgAlt: 'Woman smiling outdoors in natural light',
    linkHref: '/demo/fertility/home',
    features: [
      'IVF cycle map from consult to beta day',
      'Saved medication protocols, doses, and reminders',
      'One place for notes, labs, and big questions',
      'Step‑by‑step prep so you know what to expect',
    ],
    linkColor: '#D4537E',
  },
  {
    id: 'ttc',
    emoji: '💜',
    title: 'Trying to Conceive',
    sub: 'Cycle tracking · Ovulation · TTC support',
    ageBadge: '20s → 40s',
    badgeBg: '#EDE8FE',
    badgeColor: '#4A30A0',
    accentColor: '#7F77DD',
    img: '/images/chapter-heroes/ttc-homepage.png',
    /** Center-weighted crop so faces + pregnancy test stay visible in the card (solo pages still use cycle.png). */
    imgObjectPosition: 'center 40%',
    imgAlt: 'Couple celebrating a positive pregnancy test',
    linkHref: '/demo/trying-to-conceive/home',
    features: [
      'Estimate fertile windows from your real cycles',
      'Log LH tests and what you tried each month',
      'Arrive with a clear TTC timeline for your doctor',
      'Reminders for fertile days and follow-ups',
    ],
    linkColor: '#7F77DD',
  },
  {
    id: 'pregnancy',
    emoji: '🤰',
    title: 'Pregnancy',
    sub: 'Week by week · Appointments · Birth plan',
    ageBadge: 'Weeks 1 → Postpartum',
    badgeBg: '#E1F5EE',
    badgeColor: '#0F6E56',
    accentColor: '#1D9E75',
    img: '/images/chapter-heroes/pregnancy-homepage.png',
    /** Homepage-only hero; landscape couple — keep focal weight on faces and bump. */
    imgObjectPosition: 'center 42%',
    imgAlt:
      "A happy interracial couple smiling and cradling the woman's pregnant belly while sitting on a couch in a bright, modern living room",
    linkHref: '/demo/pregnancy/home',
    features: [
      'Week-by-week fetal development and what to expect',
      'Symptom tracker and appointment reminders',
      'Birth plan builder and hospital bag checklist',
      "Nova flags what's normal and when to call your OB",
    ],
    linkColor: '#1D9E75',
  },
  {
    id: 'postpartum',
    emoji: '🤱',
    title: 'Motherhood',
    sub: 'Postpartum · Sleep · Milestones · Wellbeing',
    ageBadge: 'Year 1 → Teen Years',
    badgeBg: '#FDE8F0',
    badgeColor: '#9B3060',
    accentColor: '#D4537E',
    img: '/images/chapter-heroes/motherhood-homepage.png',
    /** Slightly lower focal point than `top center` so more of mom shows in the crop (same box height). */
    imgObjectPosition: 'center 42%',
    imgAlt: 'Motherhood — mother and baby',
    /** Marketing chapter lives at /chapter/motherhood (card id stays postpartum for launch badge data). */
    learnMorePath: '/chapter/motherhood',
    linkHref: '/demo/motherhood/home',
    features: [
      'Sleep tracking, feeding logs, and milestone alerts',
      'Postpartum recovery and mental health check-ins',
      'Age-matched parenting guidance as your child grows',
      "Community by your baby's exact age and stage",
    ],
    linkColor: '#D4537E',
  },
];

export default function ChaptersSection() {
  const m = useMediaQuery('(max-width: 767px)');

  return (
    <section
      id="woman-chapters"
      style={{
        backgroundColor: 'rgb(249, 246, 241)',
        // Match Features / Nova / Community horizontal + vertical band (was 96px top + 1152px max, misaligned copy).
        padding: m ? '48px 18px' : '64px 48px',
        scrollMarginTop: 88,
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: m ? '0 6px' : 0,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: m ? 40 : 56 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'rgb(136, 135, 128)',
              marginBottom: 12,
              maxWidth: 640,
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.45,
            }}
          >
            One platform · four chapters of your journey
          </p>
          <h2
            className="serif"
            style={{
              fontSize: m ? 'clamp(28px, 8vw, 34px)' : 'clamp(32px, 3.5vw, 40px)',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'rgb(26, 26, 26)',
              marginBottom: 12,
            }}
          >
            Four chapters.
            <br />
            <em style={{ color: 'rgb(212, 83, 126)', fontStyle: 'italic' }}>One continuous story.</em>
          </h2>
          <p
            style={{
              fontSize: 15,
              color: 'rgb(95, 94, 90)',
              lineHeight: 1.65,
              maxWidth: 560,
              margin: '0 auto',
            }}
          >
            Fertility tools end at conception. Pregnancy apps end at delivery. Postpartum tools assume the rest went smoothly.
            We&apos;re the one place that holds all four — fertility, TTC, pregnancy, motherhood.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: m ? '1fr' : '1fr 1fr',
            gap: 16,
          }}
        >
          {chapters.map((chapter) => (
            <div
              key={chapter.title}
              style={{
                borderRadius: 20,
                overflow: 'hidden',
                border: '0.5px solid rgb(232, 224, 216)',
                background: 'rgb(255, 253, 249)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <img
                src={chapter.img}
                alt={chapter.imgAlt}
                style={{
                  width: '100%',
                  height: m ? 200 : 220,
                  objectFit: 'cover',
                  objectPosition: chapter.imgObjectPosition ?? 'top center',
                  display: 'block',
                  flexShrink: 0,
                }}
              />

              <div
                style={{
                  padding: '24px 28px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 10,
                    alignSelf: 'flex-start',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '3px 10px',
                      borderRadius: 20,
                      backgroundColor: chapter.badgeBg,
                      color: chapter.badgeColor,
                    }}
                  >
                    {chapter.ageBadge}
                  </span>
                  <ChapterLaunchBadge tone="card">
                    {launchBadgeForChapterId(chapter.id)}
                  </ChapterLaunchBadge>
                </div>

                <h3
                  className="serif"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: 'rgb(26, 26, 26)',
                    marginBottom: 4,
                    lineHeight: 1.25,
                  }}
                >
                  {chapter.emoji} {chapter.title}
                </h3>

                <p
                  style={{
                    fontSize: 11,
                    color: 'rgb(136, 135, 128)',
                    marginBottom: 16,
                  }}
                >
                  {chapter.sub}
                </p>

                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    flex: 1,
                  }}
                >
                  {chapter.features.map((f) => (
                    <li
                      key={f}
                      style={{
                        fontSize: 13,
                        color: 'rgb(61, 61, 58)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 9,
                        lineHeight: 1.45,
                      }}
                    >
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          backgroundColor: chapter.accentColor,
                          flexShrink: 0,
                        }}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                {chapter.linkHref ? (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', alignSelf: 'flex-start' }}>
                    <Link
                      to={chapter.learnMorePath ?? `/chapter/${chapter.id}`}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: chapter.linkColor,
                        background: 'transparent',
                        border: `1px solid ${chapter.accentColor}55`,
                        padding: '9px 14px',
                        borderRadius: 999,
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: 1,
                      }}
                    >
                      Learn more
                    </Link>
                  </div>
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'rgb(136, 135, 128)',
                      alignSelf: 'flex-start',
                      cursor: 'default',
                      userSelect: 'none',
                    }}
                  >
                    Demo coming soon
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
